import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import { useSetAtom } from 'jotai';
import React, { useCallback } from 'react';

import { Box, Button, EntryList, Flex, Icon, Name, Text } from '~/components/blocks';
import { ContactMethods } from '~/components/partials/PatientDetailDialog/PatientPane/ContactMethods';
import { MedixsPatientDetailDialogProfileFragment } from '~/graphql/generated/graphql';
import { patientDetailDialogPatientMobilePhoneState } from '~/state/partials/patient_detail_dialog/atoms';
import { Label } from '~/utils/label';

import { ProfileSkeleton } from '../PatientProfile/ProfileSkeleton';

type Props = {
  icon?: boolean;
  loading?: boolean;
  patient: MedixsPatientDetailDialogProfileFragment;
  visitedAt?: string;
};

const PatientAge = styled(Text)(() =>
  css({
    lineHeight: 1,
  }),
);

const EditButton = styled(Button)`
  padding: 0 6px;
  margin-left: ${({ theme }) => theme.space.s};
  transform: scale(0.85);
  transform-origin: left center;
`;

export const MedixsPatientProfile = React.memo((props: Props) => {
  const { loading, patient, visitedAt } = props;
  const theme = useTheme();
  const canEdit = patient.guestPatients.length > 0;
  const tel = patient.mobilePhone;
  const setPatientDetailDialogPatientMobilePhoneState = useSetAtom(
    patientDetailDialogPatientMobilePhoneState,
  );

  const hasApp = !!patient.patient;
  const hasSms = patient.guestPatients.some(
    (guestPatient) => guestPatient.__typename !== 'LineVisitor',
  );
  const hasLine = patient.guestPatients.some(
    (guestPatient) => guestPatient.__typename === 'LineVisitor' && guestPatient.active,
  );
  const isDeactivatedPatient = hasApp && !patient.patient?.active;

  const familyName = patient.patientName.split(/\s+/)[0];
  const givenName = patient.patientName.split(/\s+/)[1];
  const phoneticFamilyName = patient.patientNameKana.split(/\s+/)[0];
  const phoneticGivenName = patient.patientNameKana.split(/\s+/)[1];

  const handleEdit = useCallback(() => {
    setPatientDetailDialogPatientMobilePhoneState((currentState) => ({
      ...currentState,
      isOpen: true,
    }));
  }, [setPatientDetailDialogPatientMobilePhoneState]);
  return !loading ? (
    <Box>
      <Flex alignItems="end" marginBottom={theme.space.l}>
        <Name
          size="l"
          familyName={familyName}
          givenName={givenName}
          phoneticFamilyName={phoneticFamilyName}
          phoneticGivenName={phoneticGivenName}
        />
        <PatientAge marginLeft={theme.space.m}>
          {`${Label.age(patient.birthDate)}（${Label.sex(patient.gender)}）`}
        </PatientAge>
      </Flex>

      <EntryList size="s">
        <EntryList.Head>生年月日</EntryList.Head>
        <EntryList.Body>{Label.warekiBirthDate(patient.birthDate)}</EntryList.Body>
      </EntryList>
      <EntryList size="s">
        <EntryList.Head>
          <Flex alignItems="center">
            <Text>電話番号（携帯）</Text>
            {canEdit && (
              <EditButton onClick={handleEdit}>
                <Icon icon="edit" size="xs" />
                <Text size="xs">編集</Text>
              </EditButton>
            )}
          </Flex>
        </EntryList.Head>
        <EntryList.Body>
          <Flex>
            <Text>{tel || '未登録'}</Text>
          </Flex>
        </EntryList.Body>
      </EntryList>

      {visitedAt && (
        <EntryList size="s">
          <EntryList.Head>最終来局日</EntryList.Head>
          <EntryList.Body>{Label.YYYYMMDDja(visitedAt)}</EntryList.Body>
        </EntryList>
      )}
      <EntryList size="s">
        <EntryList.Head>連絡ツール</EntryList.Head>
        <EntryList.Body>
          <ContactMethods
            hasSms={hasSms}
            hasApp={hasApp}
            hasLine={hasLine}
            isDeactivatedPatient={isDeactivatedPatient}
            isDeactivatedLineVisitor={!hasLine}
          />
        </EntryList.Body>
      </EntryList>
    </Box>
  ) : (
    <ProfileSkeleton />
  );
});

MedixsPatientProfile.displayName = 'MedixsPatientProfile';
